/**
 * API Client for Core Labs
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
    || (['localhost', '127.0.0.1'].includes(window.location.hostname)
        ? `http://${window.location.hostname}:8001`
        : '');
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            // Redirect to login if not already there
            if (!window.location.pathname.includes('/login')) {
                const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                window.location.href = `/login?return=${encodeURIComponent(returnPath)}`;
            }
        }
        return Promise.reject(error);
    }
);

// ============ AUTH ============
export const authApi = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    verifyEmail: (token) => api.post('/auth/verify-email', { token }),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
    getMe: () => api.get('/auth/me', { skipAuthRedirect: true }),
};

// ============ PORTAL ============
export const portalApi = {
    getProfile: () => api.get('/portal/profile'),
    updateProfile: (data) => api.put('/portal/profile', data),
    getCompany: () => api.get('/portal/company'),
    createCompany: (data) => api.post('/portal/company', data),
    updateCompany: (data) => api.put('/portal/company', data),
    completeOnboarding: () => api.post('/portal/complete-onboarding'),
    requestService: (serviceId, data) => api.post(`/portal/services/${serviceId}/request`, data),
    createPayPalOrder: (serviceId, consent) => api.post('/portal/payments/paypal/orders', { service_id: serviceId, ...consent }),
    capturePayPalOrder: (orderId) => api.post(`/portal/payments/paypal/orders/${orderId}/capture`),
    getTickets: (status) => api.get('/portal/tickets', { params: { status } }),
    getTicket: (id) => api.get(`/portal/tickets/${id}`),
    createTicket: (data) => api.post('/portal/tickets', data),
    addMessage: (ticketId, data) => api.post(`/portal/tickets/${ticketId}/messages`, data),
    exportData: () => api.get('/portal/export'),
    
    // Customer Assets (Downloads)
    getAssets: () => api.get('/portal/assets'),
    downloadAsset: (id) => api.get(`/portal/assets/${id}/download`, { responseType: 'blob' }),
    
    // Invoices
    getInvoices: () => api.get('/portal/invoices'),
    getAccountingInvoices: () => api.get('/portal/accounting/invoices'),
    downloadAccountingInvoicePdf: (id) => api.get(`/portal/accounting/invoices/${id}/download-pdf`, { responseType: 'blob' }),
    downloadAccountingInvoiceXml: (id) => api.get(`/portal/accounting/invoices/${id}/download-xml`, { responseType: 'blob' }),
    createInvoicePayPalOrder: (id) => api.post(`/portal/accounting/invoices/${id}/paypal/order`),
    captureInvoicePayPalOrder: (id, orderId) => api.post(`/portal/accounting/invoices/${id}/paypal/orders/${orderId}/capture`),

    // Offers and reviews
    getQuotes: () => api.get('/portal/accounting/quotes'),
    downloadQuotePdf: (id) => api.get(`/portal/accounting/quotes/${id}/download-pdf`, { responseType: 'blob' }),
    acceptQuote: (id, data) => api.post(`/portal/accounting/quotes/${id}/accept`, data),
    rejectQuote: (id) => api.post(`/portal/accounting/quotes/${id}/reject`),
    getReviews: () => api.get('/portal/accounting/reviews'),
    submitReview: (id, data) => api.post('/portal/accounting/reviews', { ...data, review_id: id }),
    submitGameServerReview: (gameServerTrialId, data) => api.post('/portal/accounting/reviews', {
        ...data, game_server_trial_id: gameServerTrialId,
    }),
    
    // Contracts
    getContracts: () => api.get('/portal/contracts'),
    getContract: (id) => api.get(`/portal/contracts/${id}`),
    downloadContractPdf: (id) => api.get(`/portal/contracts/${id}/download`, { responseType: 'blob' }),
    uploadSignedPdf: (id, formData) => api.post(`/portal/contracts/${id}/upload-signed`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    consentSign: (id, data) => api.post(`/portal/contracts/${id}/consent`, data),
    signContract: (id, data) => api.post(`/portal/contracts/${id}/sign`, data),
    downloadSignedPdf: (id) => api.get(`/portal/contracts/${id}/download-signed`, { responseType: 'blob' }),

    // 24-hour game server trials
    requestGameServerTrial: (templateId, svLicenseKey = '', extraStorageMb = 10240) => api.post('/portal/game-server-trials', {
        template_id: templateId,
        extra_storage_mb: extraStorageMb,
        terms_accepted: true,
        ...(svLicenseKey ? { sv_license_key: svLicenseKey.trim() } : {}),
    }),
    getGameServers: () => api.get('/portal/game-servers'),
    getGameServerOrderConfig: () => api.get('/portal/game-server-orders/config'),
    quoteGameServerPurchase: data => api.post('/portal/game-server-orders/quote', data),
    createGameServerPurchaseOrder: data => api.post('/portal/game-server-orders/paypal/order', data),
    captureGameServerPurchaseOrder: (id, orderId) => api.post(`/portal/game-server-orders/${id}/paypal/order/${orderId}/capture`),
    getWallet: () => api.get('/portal/wallet'),
    createWalletTopupOrder: data => api.post('/portal/wallet/paypal/order', data),
    captureWalletTopupOrder: orderId => api.post(`/portal/wallet/paypal/order/${orderId}/capture`),
    quoteNewFlexGameServer: data => api.post('/portal/flex-game-server-orders/quote', data),
    createNewFlexGameServer: (data, idempotencyKey) => api.post(
        '/portal/flex-game-server-orders', data,
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    getGameServer: (id) => api.get(`/portal/game-servers/${id}`),
    getGameServerLogs: (id) => api.get(`/portal/game-servers/${id}/logs`),
    getFlexConversionQuote: (id) => api.get(`/portal/game-servers/${id}/flex/conversion-quote`),
    convertGameServerToFlex: (id, data, idempotencyKey) => api.post(
        `/portal/game-servers/${id}/flex/convert`, data,
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    powerGameServer: (id, action, options = {}) => api.post(
        `/portal/game-servers/${id}/power`,
        { action, ...(options.consent || {}) },
        { headers: options.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {} },
    ),
    pauseFlexGameServer: (id, idempotencyKey) => api.post(
        `/portal/game-servers/${id}/flex/pause-reserved`, {},
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    keepFlexGameServerData: (id) => api.post(`/portal/game-servers/${id}/flex/keep-data`),
    pauseGameServer: (id) => api.post(`/portal/game-servers/${id}/pause`),
    resumeGameServer: (id) => api.post(`/portal/game-servers/${id}/resume`),
    reinstallGameServer: (id, templateId, svLicenseKey = '') => api.post(`/portal/game-servers/${id}/reinstall`, {
        template_id: templateId,
        confirm_data_loss: true,
        ...(svLicenseKey ? { sv_license_key: svLicenseKey.trim() } : {}),
    }),
    deleteGameServer: (id, confirmName = '') => api.delete(`/portal/game-servers/${id}`, {
        params: confirmName ? { confirm_name: confirmName } : {},
    }),
    getGameServerBackups: (id) => api.get(`/portal/game-servers/${id}/backups`),
    createGameServerBackup: (id) => api.post(`/portal/game-servers/${id}/backups`),
    restoreGameServerBackup: (serverId, backupId, idempotencyKey) => api.post(
        `/portal/game-servers/${serverId}/backups/${backupId}/restore`, {},
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    uploadGameServerBackup: (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/portal/game-servers/${id}/backup-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    downloadGameServerBackup: (serverId, backupId) => api.get(`/portal/game-servers/${serverId}/backups/${backupId}/download`, { responseType: 'blob' }),
    downloadGameServerDataExport: id => api.get(`/portal/game-servers/${id}/export.zip`, { responseType: 'blob' }),
    rotateGameServerFileAccess: (id) => api.post(`/portal/game-servers/${id}/file-access/rotate`),
    getGameServerFiles: (id, path = '', query = '') => api.get(`/portal/game-servers/${id}/files`, {
        params: { path, ...(query ? { query } : {}) },
    }),
    getGameServerFileText: (id, path) => api.get(`/portal/game-servers/${id}/files/text`, { params: { path } }),
    downloadGameServerFile: (id, path) => api.get(`/portal/game-servers/${id}/files/download`, {
        params: { path }, responseType: 'blob',
    }),
    uploadGameServerFile: (id, path, file, { sha256, etag, overwrite = false } = {}) => api.put(
        `/portal/game-servers/${id}/files/upload`, file,
        {
            params: { path },
            headers: {
                'Content-Type': file?.type || 'application/octet-stream',
                ...(sha256 ? { 'X-Content-SHA256': sha256 } : {}),
                ...(etag ? { 'If-Match': etag } : {}),
                ...(overwrite ? { 'X-CoreLabs-Overwrite': 'true' } : {}),
            },
        },
    ),
    saveGameServerFileText: (id, path, content, etag) => api.put(
        `/portal/game-servers/${id}/files/text`, { content, etag }, { params: { path } },
    ),
    createGameServerFolder: (id, path) => api.post(`/portal/game-servers/${id}/files/mkdir`, { path }),
    renameGameServerFile: (id, path, newPath, etag) => api.post(`/portal/game-servers/${id}/files/rename`, {
        path, new_path: newPath, etag,
    }),
    deleteGameServerFile: (id, path, etag) => api.delete(`/portal/game-servers/${id}/files`, {
        params: { path, ...(etag ? { etag } : {}) },
    }),
    requestGameServerSupport: (id, data) => api.post(`/portal/game-servers/${id}/support`, data),
    requestGameServerResourceUpgrade: (id, data) => api.post(`/portal/game-servers/${id}/resource-upgrade-request`, data),
    quoteGameServerMutation: (id, target, idempotencyKey) => api.post(
        `/portal/game-servers/${id}/mutations/quote`, typeof target === 'object' ? target : { max_players: target },
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    applyGameServerMutation: (id, operationId, idempotencyKey, data = { restart_confirmed: true }) => api.post(
        `/portal/game-servers/${id}/mutations/${operationId}/apply`, data,
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    quoteGameServerExtension: (id, data) => api.post(`/portal/game-servers/${id}/quote`, data),
    quoteGameServerArchive: (id, packageId) => api.post(`/portal/game-servers/${id}/archive/quote`, { package_id: packageId }),
    createGameServerExtensionOrder: (id, data) => api.post(`/portal/game-servers/${id}/extensions/paypal/order`, data),
    captureGameServerExtensionOrder: (id, orderId) => api.post(`/portal/game-servers/${id}/extensions/paypal/order/${orderId}/capture`),
};

// ============ ADMIN ============
export const adminApi = {
    // Website audit tool
    getWebsiteAudits: () => api.get('/admin/website-audits', { params: { limit: 100 } }),
    getWebsiteAudit: (id) => api.get(`/admin/website-audits/${id}`),

    // Pages
    getPages: (status) => api.get('/admin/pages', { params: { status } }),
    getPage: (id) => api.get(`/admin/pages/${id}`),
    createPage: (data) => api.post('/admin/pages', data),
    updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
    deletePage: (id) => api.delete(`/admin/pages/${id}`),
    generatePreview: (id) => api.post(`/admin/pages/${id}/preview`),
    getPageRevisions: (id) => api.get(`/admin/pages/${id}/revisions`),
    getRevisionDetail: (pageId, revId) => api.get(`/admin/pages/${pageId}/revisions/${revId}`),
    rollbackPage: (pageId, revisionId) => api.post(`/admin/pages/${pageId}/rollback/${revisionId}`),
    
    // Software
    getSoftware: () => api.get('/admin/software'),
    getSoftwareItem: (id) => api.get(`/admin/software/${id}`),
    createSoftware: (data) => api.post('/admin/software', data),
    updateSoftware: (id, data) => api.put(`/admin/software/${id}`, data),
    deleteSoftware: (id) => api.delete(`/admin/software/${id}`),
    
    // Downloads
    getDownloads: (softwareId) => api.get(`/admin/software/${softwareId}/downloads`),
    createDownload: (softwareId, data) => api.post(`/admin/software/${softwareId}/downloads`, data),
    updateDownload: (downloadId, data) => api.put(`/admin/downloads/${downloadId}`, data),
    deleteDownload: (downloadId) => api.delete(`/admin/downloads/${downloadId}`),
    
    // Case Studies
    getCaseStudies: () => api.get('/admin/case-studies'),
    getCaseStudy: (id) => api.get(`/admin/case-studies/${id}`),
    createCaseStudy: (data) => api.post('/admin/case-studies', data),
    updateCaseStudy: (id, data) => api.put(`/admin/case-studies/${id}`, data),
    deleteCaseStudy: (id) => api.delete(`/admin/case-studies/${id}`),
    
    // Tickets
    getTickets: (params) => api.get('/admin/tickets', { params }),
    archiveTicket: (id) => api.put(`/admin/tickets/${id}/archive`),
    restoreArchiveTicket: (id) => api.put(`/admin/tickets/${id}/restore-archive`),
    softDeleteTicket: (id, reason) => api.delete(`/admin/tickets/${id}`, { params: { reason } }),
    restoreTicket: (id) => api.put(`/admin/tickets/${id}/restore`),
    bulkTicketAction: (data) => api.post('/admin/tickets/bulk', data),
    getTicket: (id) => api.get(`/admin/tickets/${id}`),
    updateTicket: (id, data) => api.put(`/admin/tickets/${id}`, data),
    addMessage: (ticketId, data) => api.post(`/admin/tickets/${ticketId}/messages`, data),
    
    // Users
    getUsers: (role) => api.get('/admin/users', { params: { role } }),
    createUser: (data) => api.post('/admin/users', data),
    getUser: (id) => api.get(`/admin/users/${id}`),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    
    // Menus
    getMenus: () => api.get('/admin/menus'),
    getMenu: (location) => api.get(`/admin/menus/${location}`),
    updateMenu: (location, data) => api.put(`/admin/menus/${location}`, data),
    
    // Settings
    getGlobalSettings: () => api.get('/admin/settings/global'),
    updateGlobalSettings: (data) => api.put('/admin/settings/global', data),
    getSystemSettings: () => api.get('/admin/settings/system'),
    updateSystemSettings: (data) => api.put('/admin/settings/system', data),
    testEmail: () => api.post('/admin/settings/test-email'),
    
    // System
    getSystemStatus: () => api.get('/admin/system/status'),
    getAuditLog: (params) => api.get('/admin/audit-log', { params }),
    
    // Redirects
    getRedirects: () => api.get('/admin/redirects'),
    createRedirect: (data) => api.post('/admin/redirects', data),
    deleteRedirect: (id) => api.delete(`/admin/redirects/${id}`),
    
    // Media
    getMedia: (folder) => api.get('/admin/media', { params: { folder } }),
    uploadMedia: (formData) => api.post('/admin/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    updateMedia: (id, data) => api.put(`/admin/media/${id}`, data),
    deleteMedia: (id) => api.delete(`/admin/media/${id}`),
    
    // Forms
    getForms: () => api.get('/admin/forms'),
    getForm: (id) => api.get(`/admin/forms/${id}`),
    createForm: (data) => api.post('/admin/forms', data),
    updateForm: (id, data) => api.put(`/admin/forms/${id}`, data),
    deleteForm: (id) => api.delete(`/admin/forms/${id}`),
    getFormSubmissions: (formId) => api.get(`/admin/forms/${formId}/submissions`),
    getFormSubmission: (formId, subId) => api.get(`/admin/forms/${formId}/submissions/${subId}`),
    updateFormSubmission: (formId, subId, data) => api.put(`/admin/forms/${formId}/submissions/${subId}`, data),
    
    // Download File Upload
    uploadDownloadFile: (formData) => api.post('/admin/downloads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    
    // Download Statistics
    getDownloadStats: (params) => api.get('/admin/downloads/stats', { params }),
    getSoftwareDownloadStats: (softwareId, days = 30) => 
        api.get(`/admin/software/${softwareId}/download-stats`, { params: { days } }),
    
    // Customer Assets (Admin)
    getCompanies: () => api.get('/admin/companies'),
    getCompanyAssets: (companyId) => api.get(`/admin/companies/${companyId}/assets`),
    uploadCompanyAsset: (companyId, formData) => api.post(`/admin/companies/${companyId}/assets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    deleteAsset: (assetId) => api.delete(`/admin/assets/${assetId}`),
    
    // Chunked Upload for large files (5GB+)
    initChunkedUpload: (data) => api.post('/admin/downloads/upload/init', data),
    uploadChunk: (uploadId, chunkIndex, chunk) => {
        const formData = new FormData();
        formData.append('chunk_index', chunkIndex);
        formData.append('chunk', chunk);
        return api.post(`/admin/downloads/upload/chunk/${uploadId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    completeChunkedUpload: (uploadId, checksum) => {
        const formData = new FormData();
        if (checksum) formData.append('expected_checksum', checksum);
        return api.post(`/admin/downloads/upload/complete/${uploadId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getUploadStatus: (uploadId) => api.get(`/admin/downloads/upload/status/${uploadId}`),
    cancelUpload: (uploadId) => api.delete(`/admin/downloads/upload/cancel/${uploadId}`),
    
    // Privacy
    getConsentLogs: (limit) => api.get('/admin/privacy/consent-logs', { params: { limit } }),
    
    // Homepage Content (Admin)
    getHomepageContent: () => api.get('/admin/homepage-content'),
    updateHomepageContent: (data) => api.put('/admin/homepage-content', data),
    
    // Company Data (Admin)
    getCompanyData: () => api.get('/admin/company-data'),
    updateCompanyData: (data) => api.put('/admin/company-data', data),
    getSetupStatus: () => api.get('/admin/setup-status'),
    markSetupComplete: () => api.post('/admin/setup-complete'),
    
    // Analytics Dashboard
    getAnalytics: (params) => api.get('/admin/analytics', { params }),
    
    // Content Seeding (Admin)
    seedContent: () => api.post('/admin/content/seed'),
    getContentStats: () => api.get('/admin/content/stats'),
    
    // Services Catalog
    getServices: () => api.get('/admin/services'),
    getService: (id) => api.get(`/admin/services/${id}`),
    createService: (data) => api.post('/admin/services', data),
    updateService: (id, data) => api.put(`/admin/services/${id}`, data),
    deleteService: (id) => api.delete(`/admin/services/${id}`),
    
    // Contracts
    getContractTemplates: () => api.get('/admin/contract-templates'),
    createContractTemplate: (data) => api.post('/admin/contract-templates', data),
    reviseContractTemplate: (id, data) => api.post(`/admin/contract-templates/${id}/revisions`, data),
    archiveContractTemplate: (id) => api.delete(`/admin/contract-templates/${id}`),
    getContracts: (status) => api.get('/admin/contracts', { params: { status } }),
    getContract: (id) => api.get(`/admin/contracts/${id}`),
    createContract: (data) => api.post('/admin/contracts', data),
    updateContract: (id, data) => api.put(`/admin/contracts/${id}`, data),
    deleteContract: (id) => api.delete(`/admin/contracts/${id}`),
    generateContractPdf: (id) => api.post(`/admin/contracts/${id}/generate-pdf`),
    reviewContractSignature: (id, decision, reason = '') =>
        api.post(`/admin/contracts/${id}/signature-review`, { decision, reason }),
    downloadContractPdf: (id) => api.get(`/admin/contracts/${id}/download-pdf`, { responseType: 'blob' }),
    downloadSignedPdf: (id) => api.get(`/admin/contracts/${id}/download-signed`, { responseType: 'blob' }),

    // Accounting & sales workflow
    getAccountingDashboard: () => api.get('/admin/accounting/dashboard'),
    getAccountingSettings: () => api.get('/admin/accounting/settings'),
    updateAccountingSettings: (data) => api.put('/admin/accounting/settings', data),
    getSalesCases: (params) => api.get('/admin/accounting/cases', { params }),
    getQuotes: (params) => api.get('/admin/accounting/quotes', { params }),
    createQuote: (data) => api.post('/admin/accounting/quotes', data),
    updateQuote: (id, data) => api.put(`/admin/accounting/quotes/${id}`, data),
    issueQuote: (id) => api.post(`/admin/accounting/quotes/${id}/issue`),
    quoteToContract: (id, data = {}) => api.post(`/admin/accounting/quotes/${id}/contract`, data),
    createQuoteFromTicket: (ticketId, data = {}) => api.post(`/admin/accounting/tickets/${ticketId}/quote`, data),
    createContractFromTicket: (ticketId, data = {}) => api.post(`/admin/accounting/tickets/${ticketId}/contract`, data),
    getAccountingInvoices: (params) => api.get('/admin/accounting/invoices', { params }),
    getRecurringInvoices: () => api.get('/admin/accounting/recurring'),
    generateRecurringInvoices: (data = {}) => api.post('/admin/accounting/recurring/generate', data),
    createAccountingInvoice: (data) => api.post('/admin/accounting/invoices', data),
    updateAccountingInvoice: (id, data) => api.put(`/admin/accounting/invoices/${id}`, data),
    issueAccountingInvoice: (id) => api.post(`/admin/accounting/invoices/${id}/issue`),
    correctAccountingInvoice: (id, data) => api.post(`/admin/accounting/invoices/${id}/correction`, data),
    recordAccountingPayment: (id, data) => api.post(`/admin/accounting/invoices/${id}/payment`, data),
    downloadAccountingInvoicePdf: (id) => api.get(`/admin/accounting/invoices/${id}/download-pdf`, { responseType: 'blob' }),
    downloadAccountingInvoiceXml: (id) => api.get(`/admin/accounting/invoices/${id}/download-xml`, { responseType: 'blob' }),
    getIncomingInvoices: (params) => api.get('/admin/accounting/incoming', { params }),
    createIncomingInvoice: (data) => api.post('/admin/accounting/incoming', data),
    uploadIncomingInvoiceDocument: (id, data) => api.post(`/admin/accounting/incoming/${id}/document`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
    bookIncomingInvoice: (id) => api.post(`/admin/accounting/incoming/${id}/book`),
    getJournal: (params) => api.get('/admin/accounting/journal', { params }),
    createJournalEntry: (data) => api.post('/admin/accounting/journal', data),
    postJournalEntry: (id) => api.post(`/admin/accounting/journal/${id}/post`),
    reverseJournalEntry: (id, data) => api.post(`/admin/accounting/journal/${id}/reverse`, data),
    getAccountingPeriods: () => api.get('/admin/accounting/periods'),
    updateAccountingPeriod: (year, month, status) => api.put(`/admin/accounting/periods/${year}/${month}`, { status }),
    exportAccounting: (params) => api.get('/admin/accounting/export', { params, responseType: 'blob' }),
    getAccountingReviews: (params) => api.get('/admin/accounting/reviews', { params }),
    moderateReview: (id, data) => api.patch(`/admin/accounting/reviews/${id}`, data),
    
    // FAQs
    getFaqs: () => api.get('/admin/faqs'),
    createFaq: (data) => api.post('/admin/faqs', data),
    updateFaq: (id, data) => api.put(`/admin/faqs/${id}`, data),
    deleteFaq: (id) => api.delete(`/admin/faqs/${id}`),

    // Game server trials
    getGameServerTemplates: () => api.get('/admin/game-server-templates'),
    createGameServerTemplate: (data) => api.post('/admin/game-server-templates', data),
    updateGameServerTemplate: (id, data) => api.put(`/admin/game-server-templates/${id}`, data),
    deleteGameServerTemplate: (id) => api.delete(`/admin/game-server-templates/${id}`),
    getGameServerInstances: () => api.get('/admin/game-server-instances'),
    getGameServerWallets: () => api.get('/admin/game-server-wallets'),
    correctGameServerWallet: (data, idempotencyKey) => api.post(
        '/admin/game-server-wallets/corrections', data,
        { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
    getGameServerInstanceLogs: (id) => api.get(`/admin/game-server-instances/${id}/logs`),
    createGameServerInstance: (data) => api.post('/admin/game-server-instances', data),
    retryGameServerInstance: (id) => api.post(`/admin/game-server-instances/${id}/retry`),
    stopGameServerInstance: (id) => api.post(`/admin/game-server-instances/${id}/stop`),
    deleteGameServerInstance: (id) => api.post(`/admin/game-server-instances/${id}/delete`),
    migrateGameServerInstance: (id, targetNodeId) => api.post(`/admin/game-server-instances/${id}/migrate`, { target_node_id: targetNodeId }),
    resizeGameServerInstance: (id, data) => api.put(`/admin/game-server-instances/${id}/resources`, data),
    getGameServerNodes: () => api.get('/admin/game-server-nodes'),
    getGameServerNodeDiagnostics: (id) => api.get(`/admin/game-server-nodes/${id}/diagnostics`),
    getGameServerNodeMaintenanceJobs: (id) => api.get(`/admin/game-server-nodes/${id}/maintenance-jobs`),
    createGameServerNodeMaintenanceJob: (id, action) => api.post(`/admin/game-server-nodes/${id}/maintenance-jobs`, { action }),
    setGameServerNodeDrain: (id, drain) => api.post(`/admin/game-server-nodes/${id}/drain`, { drain }),
    setGameServerNodeEnabled: (id, enabled) => api.post(`/admin/game-server-nodes/${id}/enable`, { enabled }),
    prepareGameServerNodeKeyRotation: (id) => api.post(`/admin/game-server-nodes/${id}/key-rotation/prepare`),
    promoteGameServerNodeKeyRotation: (id) => api.post(`/admin/game-server-nodes/${id}/key-rotation/promote`),
    rollbackGameServerNodeKeyRotation: (id) => api.post(`/admin/game-server-nodes/${id}/key-rotation/rollback`),
    createGameServerNodeEnrollment: (expiresMinutes = 15) => api.post('/admin/game-server-nodes/enrollment-token', { expires_minutes: expiresMinutes }),
};

// ============ PUBLIC ============
export const publicApi = {
    // Free website audit
    startWebsiteAudit: (data) => api.post('/public/website-audits', data, { skipAuthRedirect: true }),
    getWebsiteAudit: (id, token) => api.get(`/public/website-audits/${id}`, {
        headers: { 'X-Audit-Token': token }, skipAuthRedirect: true,
    }),
    downloadWebsiteAuditPdf: (id, token) => api.get(`/public/website-audits/${id}/report`, {
        headers: { 'X-Audit-Token': token }, responseType: 'blob', skipAuthRedirect: true,
    }),
    requestWebsiteAuditFix: (id, token, data) => api.post(`/public/website-audits/${id}/request-fix`, data, {
        headers: { 'X-Audit-Token': token }, skipAuthRedirect: true,
    }),
    checkDomainEmailSsl: (data) => api.post('/public/tools/domain-email-ssl-check', data, { skipAuthRedirect: true }),
    createUptimeMonitor: (data) => api.post('/public/uptime-monitors', data, { skipAuthRedirect: true }),
    getUptimeMonitor: (id, token) => api.get(`/public/uptime-monitors/${id}`, {
        headers: { 'X-Monitor-Token': token }, skipAuthRedirect: true,
    }),
    getServiceStatus: () => api.get('/public/service-status', { skipAuthRedirect: true }),
    getGameServerTemplates: () => api.get('/public/game-server-templates', { skipAuthRedirect: true }),
    getGameServerCatalogAudit: () => api.get('/public/game-server-catalog-audit', { skipAuthRedirect: true }),

    // Homepage Content
    getHomepageContent: (lang = 'de') => api.get('/public/homepage-content', { params: { lang } }),
    getNavigation: (lang = 'de') => api.get('/public/navigation', { params: { lang } }),
    getPage: (slug, lang = 'de') => api.get(`/public/pages/${slug}`, { params: { lang } }),
    getLegalPage: (type) => api.get(`/public/legal/${type}`),
    submitWithdrawal: (data) => api.post('/public/withdrawal', data),
    submitCancellation: (data) => api.post('/public/cancellation', data),
    getSettings: () => api.get('/public/company-info'),
    
    // Existing endpoints
    getGlobals: (lang = 'de') => api.get('/public/globals', { params: { lang } }),
    getPageBySlug: (slug, lang = 'de') => api.get(`/public/page/${slug}`, { params: { lang } }),
    getSoftware: (lang = 'de') => api.get('/public/software', { params: { lang } }),
    getSoftwareDetail: (slug, lang = 'de') => api.get(`/public/software/${slug}`, { params: { lang } }),
    downloadSoftware: (id) => api.get(`/public/download/${encodeURIComponent(id)}`, { responseType: 'blob' }),
    getCaseStudies: (lang = 'de') => api.get('/public/case-studies', { params: { lang } }),
    getCaseStudyDetail: (slug, lang = 'de') => api.get(`/public/case-studies/${slug}`, { params: { lang } }),
    getForm: (formId, lang = 'de') => api.get(`/public/form/${formId}`, { params: { lang } }),
    submitForm: (formId, data) => api.post(`/public/form/${formId}/submit`, data),
    logConsent: (data) => api.post('/public/consent', data),
    saveConsent: (data) => api.post('/public/consent', data),
    
    // FAQs
    getFaqs: () => api.get('/public/faqs'),

    // Published, consented customer reviews
    getReviews: (params = {}) => api.get('/public/reviews', { params }),
    getGameServerReviews: (limit = 6) => api.get('/public/reviews', {
        params: { review_type: 'gameserver', limit },
    }),
    
    // Services
    getServices: () => api.get('/public/services'),
    getServiceBySlug: (slug) => api.get(`/public/services/${slug}`),
    
    // Analytics Tracking
    trackEvent: (data) => api.post('/public/analytics/track', data),
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;
